/**
 * Shared page background with subtle medical/healthcare decorative shapes.
 * Wraps page content and provides the consistent brand visual treatment.
 */
const PageBackground = ({ children }) => {
  return (
    <div className="mesh-gradient min-h-screen relative overflow-x-hidden">
      {/* Subtle medical shapes — decorative, non-distracting */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
        {/* Heartbeat / pulse line — top right */}
        <svg
          className="absolute -top-4 right-[5%] w-[400px] h-[100px] text-brand-700/[0.04]"
          viewBox="0 0 400 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="0,50 80,50 100,20 120,80 140,35 160,65 180,50 400,50" />
        </svg>

        {/* DNA helix — left side, mid-page */}
        <svg
          className="absolute top-[30%] -left-8 w-[80px] h-[320px] text-brand-700/[0.03]"
          viewBox="0 0 80 320"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M20,0 C60,40 60,80 20,120 C-20,160 -20,200 20,240 C60,280 60,320 20,320" />
          <path d="M60,0 C20,40 20,80 60,120 C100,160 100,200 60,240 C20,280 20,320 60,320" />
          {/* Cross rungs */}
          <line x1="28" y1="30" x2="52" y2="30" />
          <line x1="22" y1="60" x2="58" y2="60" />
          <line x1="22" y1="90" x2="58" y2="90" />
          <line x1="35" y1="150" x2="45" y2="150" />
          <line x1="22" y1="180" x2="58" y2="180" />
          <line x1="22" y1="210" x2="58" y2="210" />
          <line x1="28" y1="270" x2="52" y2="270" />
          <line x1="22" y1="300" x2="58" y2="300" />
        </svg>

        {/* Cross / plus signs — scattered */}
        <svg
          className="absolute top-[55%] right-[8%] w-10 h-10 text-brand-700/[0.05]"
          viewBox="0 0 40 40"
          fill="currentColor"
        >
          <rect x="16" y="4" width="8" height="32" rx="2" />
          <rect x="4" y="16" width="32" height="8" rx="2" />
        </svg>
        <svg
          className="absolute top-[20%] left-[12%] w-6 h-6 text-brand-500/[0.04]"
          viewBox="0 0 40 40"
          fill="currentColor"
        >
          <rect x="16" y="4" width="8" height="32" rx="2" />
          <rect x="4" y="16" width="32" height="8" rx="2" />
        </svg>
        <svg
          className="absolute top-[75%] left-[20%] w-8 h-8 text-brand-700/[0.04]"
          viewBox="0 0 40 40"
          fill="currentColor"
        >
          <rect x="16" y="4" width="8" height="32" rx="2" />
          <rect x="4" y="16" width="32" height="8" rx="2" />
        </svg>

        {/* Stethoscope circle — bottom right */}
        <svg
          className="absolute bottom-[10%] right-[3%] w-[120px] h-[120px] text-brand-700/[0.03]"
          viewBox="0 0 120 120"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="60" cy="60" r="40" />
          <circle cx="60" cy="60" r="8" />
          <path d="M60,20 C60,8 75,8 75,20" />
        </svg>

        {/* Pill capsule — mid right */}
        <svg
          className="absolute top-[45%] right-[15%] w-[60px] h-[24px] text-brand-500/[0.04] rotate-[30deg]"
          viewBox="0 0 60 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="1" y="1" width="58" height="22" rx="11" />
          <line x1="30" y1="1" x2="30" y2="23" />
        </svg>

        {/* Heart outline — bottom left */}
        <svg
          className="absolute bottom-[25%] left-[6%] w-[50px] h-[45px] text-brand-700/[0.04]"
          viewBox="0 0 50 45"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M25,40 C10,28 0,18 0,10 C0,4 4,0 10,0 C16,0 20,4 25,10 C30,4 34,0 40,0 C46,0 50,4 50,10 C50,18 40,28 25,40Z" />
        </svg>
      </div>

      <div className="flex flex-col min-h-screen relative z-[1]">
        {children}
      </div>
    </div>
  );
};

export default PageBackground;
